// ═══════════════════════════════════════════════════════════════════════════
// Fatigue Risk Engine — BuildMyShifts
// Scores fatigue risk for individual staff and shift patterns.
// Flags dangerous patterns (back-to-back long blocks, insufficient rest,
// consecutive nights) BEFORE the rota is published.
// ═══════════════════════════════════════════════════════════════════════════

import { getBlockWorkingHours, SEGMENT_TYPES } from './shiftTypes';
import { timeToMinutes, getWeekStart } from './schedulingEngine';

/**
 * Risk levels
 */
export const FATIGUE_RISK = {
  LOW: 'low',         // 0–30   — no concerns
  MODERATE: 'moderate', // 31–60  — worth reviewing
  HIGH: 'high',        // 61–80  — flag before publishing
  CRITICAL: 'critical', // 81–100 — should not publish without override
};

/**
 * Fatigue factor weights (configurable per org)
 */
export const DEFAULT_FATIGUE_WEIGHTS = {
  consecutiveLongShifts: 25,   // Back-to-back shifts > 10 hrs
  consecutiveNights: 20,       // Consecutive night shifts
  insufficientRest: 20,        // Rest < 11 hrs between shifts
  totalWeeklyHours: 15,        // Total weekly hours nearing/exceeding 48
  sleepInFrequency: 10,        // Frequent sleep-ins in a short period
  noRestDay: 10,               // No day off within 7-day window
};

/**
 * Calculate rest hours between two shifts (on consecutive or same days)
 */
function restBetween(shiftA, shiftB) {
  const aEnd = timeToMinutes(shiftA.endTime || shiftA.segments?.[shiftA.segments.length - 1]?.endTime || '17:00');
  const bStart = timeToMinutes(shiftB.startTime || shiftB.segments?.[0]?.startTime || '09:00');

  const dateA = new Date(shiftA.date);
  const dateB = new Date(shiftB.date);
  const dayDiff = Math.round((dateB - dateA) / (1000 * 60 * 60 * 24));

  let endMinAbsolute = aEnd;
  if (timeToMinutes(shiftA.startTime || '09:00') > aEnd) endMinAbsolute += 24 * 60; // overnight shift

  const startMinAbsolute = bStart + dayDiff * 24 * 60;
  const restMins = startMinAbsolute - endMinAbsolute;

  return Math.max(0, restMins / 60);
}

/**
 * Is this a night shift? (starts at or after 8 PM, or ends at or after midnight)
 */
function isNightShift(shift) {
  const start = timeToMinutes(shift.startTime || shift.segments?.[0]?.startTime || '09:00');
  const end = timeToMinutes(shift.endTime || shift.segments?.[shift.segments?.length - 1]?.endTime || '17:00');
  return start >= 20 * 60 || end <= 8 * 60 || end < start;
}

/**
 * Is this a long shift? (> threshold hours)
 */
function isLongShift(shift, thresholdHours = 10) {
  const segments = shift.segments || [{
    type: SEGMENT_TYPES.STANDARD,
    startTime: shift.startTime,
    endTime: shift.endTime,
  }];
  const hours = getBlockWorkingHours(segments, true);
  return hours >= thresholdHours;
}

/**
 * Calculate fatigue risk score for a staff member over a date range
 * Returns { score: 0-100, level: FATIGUE_RISK.*, factors: [...], recommendations: [...] }
 */
export function calculateFatigueScore(
  staffId,
  shifts,
  dateRange = { start: '', end: '' },
  weights = DEFAULT_FATIGUE_WEIGHTS,
) {
  // Get this staff's shifts in date range, sorted by date/time
  const staffShifts = shifts
    .filter(s => {
      if (s.assignedStaffId !== staffId) return false;
      if (s.status === 'cancelled') return false;
      if (dateRange.start && s.date < dateRange.start) return false;
      if (dateRange.end && s.date > dateRange.end) return false;
      return true;
    })
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.startTime || '').localeCompare(b.startTime || '');
    });

  if (staffShifts.length === 0) {
    return { score: 0, level: FATIGUE_RISK.LOW, factors: [], recommendations: [] };
  }

  const factors = [];
  let rawScore = 0;

  // ─── Factor 1: Consecutive long shifts ──────────────────────────────
  let consecutiveLong = 0;
  let maxConsecutiveLong = 0;
  for (const shift of staffShifts) {
    if (isLongShift(shift)) {
      consecutiveLong++;
      maxConsecutiveLong = Math.max(maxConsecutiveLong, consecutiveLong);
    } else {
      consecutiveLong = 0;
    }
  }
  if (maxConsecutiveLong >= 2) {
    const severity = Math.min(1, (maxConsecutiveLong - 1) / 3);
    const score = severity * weights.consecutiveLongShifts;
    rawScore += score;
    factors.push({
      type: 'consecutiveLongShifts',
      label: `${maxConsecutiveLong} consecutive long shifts (10+ hrs)`,
      severity: severity > 0.6 ? 'high' : 'moderate',
      score: Math.round(score),
    });
  }

  // ─── Factor 2: Consecutive night shifts ─────────────────────────────
  let consecutiveNights = 0;
  let maxConsecutiveNights = 0;
  for (const shift of staffShifts) {
    if (isNightShift(shift)) {
      consecutiveNights++;
      maxConsecutiveNights = Math.max(maxConsecutiveNights, consecutiveNights);
    } else {
      consecutiveNights = 0;
    }
  }
  if (maxConsecutiveNights >= 2) {
    const severity = Math.min(1, (maxConsecutiveNights - 1) / 4);
    const score = severity * weights.consecutiveNights;
    rawScore += score;
    factors.push({
      type: 'consecutiveNights',
      label: `${maxConsecutiveNights} consecutive night shifts`,
      severity: severity > 0.6 ? 'high' : 'moderate',
      score: Math.round(score),
    });
  }

  // ─── Factor 3: Insufficient rest between shifts ─────────────────────
  let shortRestCount = 0;
  let minRestFound = Infinity;
  for (let i = 1; i < staffShifts.length; i++) {
    const rest = restBetween(staffShifts[i - 1], staffShifts[i]);
    if (rest < 11) {
      shortRestCount++;
      minRestFound = Math.min(minRestFound, rest);
    }
  }
  if (shortRestCount > 0) {
    const severity = Math.min(1, shortRestCount / 3 + (minRestFound < 8 ? 0.3 : 0));
    const score = severity * weights.insufficientRest;
    rawScore += score;
    factors.push({
      type: 'insufficientRest',
      label: `${shortRestCount} instance(s) of <11hr rest (min: ${Math.round(minRestFound * 10) / 10}hr)`,
      severity: minRestFound < 8 ? 'high' : 'moderate',
      score: Math.round(score),
    });
  }

  // ─── Factor 4: Total weekly hours ───────────────────────────────────
  // Group shifts by week and check each week
  const weekMap = {};
  for (const shift of staffShifts) {
    const ws = getWeekStart(shift.date);
    if (!weekMap[ws]) weekMap[ws] = [];
    weekMap[ws].push(shift);
  }
  let maxWeeklyHours = 0;
  for (const [, weekShifts] of Object.entries(weekMap)) {
    const totalHours = weekShifts.reduce((sum, s) => {
      const segs = s.segments || [{ type: SEGMENT_TYPES.STANDARD, startTime: s.startTime, endTime: s.endTime }];
      return sum + getBlockWorkingHours(segs, false);
    }, 0);
    maxWeeklyHours = Math.max(maxWeeklyHours, totalHours);
  }
  if (maxWeeklyHours > 40) {
    const severity = Math.min(1, (maxWeeklyHours - 40) / 20);
    const score = severity * weights.totalWeeklyHours;
    rawScore += score;
    factors.push({
      type: 'totalWeeklyHours',
      label: `Peak weekly hours: ${Math.round(maxWeeklyHours * 10) / 10}h`,
      severity: maxWeeklyHours > 48 ? 'high' : 'moderate',
      score: Math.round(score),
    });
  }

  // ─── Factor 5: Sleep-in frequency ───────────────────────────────────
  const sleepInCount = staffShifts.filter(s =>
    s.hasSleepIn || s.segments?.some(seg => seg.type === SEGMENT_TYPES.SLEEP_IN)
  ).length;
  if (sleepInCount >= 3) {
    const severity = Math.min(1, (sleepInCount - 2) / 4);
    const score = severity * weights.sleepInFrequency;
    rawScore += score;
    factors.push({
      type: 'sleepInFrequency',
      label: `${sleepInCount} sleep-in shifts in period`,
      severity: sleepInCount >= 5 ? 'high' : 'moderate',
      score: Math.round(score),
    });
  }

  // ─── Factor 6: No rest day in 7-day window ─────────────────────────
  const workingDates = new Set(staffShifts.map(s => s.date));
  let longestStreak = 0;
  let currentStreak = 0;
  const allDates = [...workingDates].sort();
  if (allDates.length > 0) {
    const first = new Date(allDates[0]);
    const last = new Date(allDates[allDates.length - 1]);
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      const ds = d.toISOString().split('T')[0];
      if (workingDates.has(ds)) {
        currentStreak++;
        longestStreak = Math.max(longestStreak, currentStreak);
      } else {
        currentStreak = 0;
      }
    }
  }
  if (longestStreak >= 6) {
    const severity = Math.min(1, (longestStreak - 5) / 3);
    const score = severity * weights.noRestDay;
    rawScore += score;
    factors.push({
      type: 'noRestDay',
      label: `${longestStreak} consecutive working days`,
      severity: longestStreak >= 7 ? 'high' : 'moderate',
      score: Math.round(score),
    });
  }

  // ─── Final score and level ──────────────────────────────────────────
  const finalScore = Math.min(100, Math.round(rawScore));
  let level;
  if (finalScore <= 30) level = FATIGUE_RISK.LOW;
  else if (finalScore <= 60) level = FATIGUE_RISK.MODERATE;
  else if (finalScore <= 80) level = FATIGUE_RISK.HIGH;
  else level = FATIGUE_RISK.CRITICAL;

  // ─── Recommendations ───────────────────────────────────────────────
  const recommendations = [];
  if (maxConsecutiveLong >= 3) {
    recommendations.push('Avoid scheduling more than 2 consecutive long shifts. Consider inserting a short shift or rest day.');
  }
  if (maxConsecutiveNights >= 3) {
    recommendations.push('Limit consecutive night shifts to 3 maximum. Ensure 48hr recovery after a night run.');
  }
  if (shortRestCount > 0) {
    recommendations.push('Ensure minimum 11 hours rest between shifts as per Working Time Directive.');
  }
  if (maxWeeklyHours > 48) {
    recommendations.push('Weekly hours exceed 48hr WTD limit. Reduce shifts or redistribute workload.');
  }
  if (longestStreak >= 7) {
    recommendations.push('Schedule at least 1 rest day per 7-day period.');
  }
  if (sleepInCount >= 4) {
    recommendations.push('High sleep-in frequency. Rotate sleep-in duties among available staff.');
  }

  return {
    score: finalScore,
    level,
    factors,
    recommendations,
    data: {
      totalShifts: staffShifts.length,
      maxConsecutiveLong,
      maxConsecutiveNights,
      shortRestCount,
      maxWeeklyHours: Math.round(maxWeeklyHours * 10) / 10,
      sleepInCount,
      longestStreak,
    },
  };
}

/**
 * Batch-calculate fatigue scores for all assigned staff in a rota
 * Returns Map<staffId, fatigueResult>
 */
export function calculateRotaFatigueRisks(shifts, dateRange) {
  const staffIds = [...new Set(shifts.filter(s => s.assignedStaffId).map(s => s.assignedStaffId))];
  const results = {};

  for (const staffId of staffIds) {
    results[staffId] = calculateFatigueScore(staffId, shifts, dateRange);
  }

  return results;
}

/**
 * Get all high/critical fatigue warnings for a rota
 * Used to warn managers before publishing
 */
export function getRotaFatigueWarnings(shifts, dateRange) {
  const risks = calculateRotaFatigueRisks(shifts, dateRange);
  const warnings = [];

  for (const [staffId, result] of Object.entries(risks)) {
    if (result.level === FATIGUE_RISK.HIGH || result.level === FATIGUE_RISK.CRITICAL) {
      const staffShifts = shifts.filter(s => s.assignedStaffId === staffId);
      const staffName = staffShifts[0]?.assignedStaffName || staffId;
      warnings.push({
        staffId,
        staffName,
        score: result.score,
        level: result.level,
        topFactors: result.factors.slice(0, 3),
        recommendations: result.recommendations,
      });
    }
  }

  return warnings.sort((a, b) => b.score - a.score);
}

const fatigueEngine = {
  FATIGUE_RISK,
  DEFAULT_FATIGUE_WEIGHTS,
  calculateFatigueScore,
  calculateRotaFatigueRisks,
  getRotaFatigueWarnings,
};
export default fatigueEngine;
